import { DefaultLogger, SafeShell, type Logger } from '../../../core';
import { PageInfoParser } from './page-info-parser';
import type { AutoPageViewOptions, PageSignal, PageSignalPage, PageViewMode } from './types';

interface PageState {
  path: string;
  mode: PageViewMode;
  isShowing: boolean;
  showSequence: number;
  collectedSequence: number;
  referrer?: string;
}

interface PendingVisit {
  instanceId: string;
  sequence: number;
  properties: Record<string, unknown>;
}

/** 只保留当前待采集访问；归属由路由规则决定，不受旧 PV 和自动回调先后影响。 */
export class PageAccessCoordinator {
  private readonly pages = new Map<string, PageState>();
  private readonly rules: Record<string, PageViewMode> = Object.create(null);
  private refPath?: string;
  private currentId?: string;
  private resumeId?: string;
  private isReady: boolean;
  private pending?: PendingVisit;
  private readyTimer?: ReturnType<typeof setTimeout>;
  private destroyed = false;
  private readonly eventName: string;
  private readonly logger: Logger;

  constructor(
    private readonly options: AutoPageViewOptions,
    private readonly parser: PageInfoParser,
    private readonly safeShell: SafeShell,
    logger?: Logger,
  ) {
    this.eventName = options.eventName ?? 'page_view';
    this.isReady = options.initiallyReady !== false;
    this.logger = logger ?? new DefaultLogger();
    for (const [path, mode] of Object.entries(options.routeRules ?? {})) {
      if (['auto', 'manual', 'off'].includes(mode)) this.rules[parser.normalize(path)] = mode;
    }
  }

  signal(signal: PageSignal): void {
    this.safeShell.run(() => {
      if (this.destroyed) return;
      if (signal.type === 'app-hide') {
        // 使用最后显示页面，兼容 Page.onHide 早于 App.onHide 的平台顺序。
        this.resumeId = this.currentId;
        const state = this.currentId ? this.pages.get(this.currentId) : undefined;
        if (state) state.isShowing = false;
        this.clearPending();
        return;
      }
      if (signal.type === 'app-show' || !('page' in signal)) return;
      if (!signal.page.instanceId || !this.parser.normalize(signal.page.path)) return;
      if (signal.type === 'show') this.handleShow(signal.page, Boolean(signal.resume));
      else if (signal.type === 'hide') this.handleHide(signal.page.instanceId);
      else {
        this.handleHide(signal.page.instanceId);
        this.pages.delete(signal.page.instanceId);
      }
    }, undefined, 'PageView.signal');
  }

  /** auto/off 的旧 PV 一律不补发；manual 保留显式调用的次数与属性。 */
  bridgeManualPageView(page: PageSignalPage): void {
    this.safeShell.run(() => {
      if (this.destroyed || !this.parser.normalize(page.path) || this.mode(page.path) !== 'manual') return;
      const state = this.pages.get(page.instanceId);
      this.options.track(this.eventName, this.properties(page, state?.referrer));
    }, undefined, 'PageView.bridge');
  }

  /** 用于显式旧事件识别后的过滤；未知事件由调用方原样保留。 */
  allowsManual(path: string): boolean {
    return this.safeShell.run(() => !this.destroyed && (!this.parser.normalize(path) || this.mode(path) === 'manual'), true, 'PageView.mode');
  }

  ready(): void {
    this.safeShell.run(() => {
      if (this.destroyed) return;
      this.isReady = true;
      const visit = this.pending;
      this.clearPending();
      if (!visit || this.currentId !== visit.instanceId) return;
      const state = this.pages.get(visit.instanceId);
      if (state?.isShowing && state.showSequence === visit.sequence) this.emit(state, visit.properties);
    }, undefined, 'PageView.ready');
  }

  destroy(): void {
    this.safeShell.run(() => {
      this.destroyed = true;
      this.clearPending();
      this.pages.clear();
      this.currentId = this.resumeId = this.refPath = undefined;
    }, undefined, 'PageView.destroy');
  }

  private handleShow(page: PageSignalPage, resume: boolean): void {
    const state = this.ensureState(page);
    if (state.isShowing) return;
    if (this.currentId && this.currentId !== page.instanceId) this.handleHide(this.currentId);
    resume ||= this.resumeId === page.instanceId;
    this.resumeId = undefined;
    this.currentId = page.instanceId;
    state.isShowing = true;
    state.showSequence += 1;
    // 单纯恢复不改变 SPA 来源链，避免 ref 指向页面自身。
    if (!resume) {
      state.referrer = this.refPath;
      this.refPath = state.path;
    }
    if ((resume && !this.options.trackOnResume) || state.mode !== 'auto') return;
    const properties = this.properties(page, state.referrer);
    if (this.isReady) this.emit(state, properties);
    else {
      this.clearPending();
      this.pending = { instanceId: page.instanceId, sequence: state.showSequence, properties };
      const configured = this.options.readyTimeoutMs;
      const timeout = configured && Number.isFinite(configured) && configured > 0 ? configured : 10000;
      this.readyTimer = setTimeout(() => {
        this.clearPending();
        this.safeShell.run(() => this.logger.warn('[AutoPageView] 等待业务初始化超时，已放弃当前访问'), undefined);
      }, timeout);
    }
  }

  private handleHide(instanceId: string): void {
    const state = this.pages.get(instanceId);
    if (state) state.isShowing = false;
    if (this.pending?.instanceId === instanceId) this.clearPending();
  }

  private emit(state: PageState, properties: Record<string, unknown>): void {
    if (this.destroyed || state.collectedSequence === state.showSequence) return;
    // 先认领当前访问，防止同步回调重入；不保留随显示次数增长的集合。
    state.collectedSequence = state.showSequence;
    this.safeShell.run(() => this.options.track(this.eventName, properties), undefined, 'PageView.track');
  }

  private ensureState(page: PageSignalPage): PageState {
    const existing = this.pages.get(page.instanceId);
    if (existing) return existing;
    const path = this.parser.normalize(page.path);
    const state: PageState = {
      path, mode: this.mode(path), isShowing: false, showSequence: 0, collectedSequence: -1,
    };
    this.pages.set(page.instanceId, state);
    return state;
  }

  private mode(path: string): PageViewMode {
    return this.rules[this.parser.normalize(path)] ?? 'auto';
  }

  private properties(page: PageSignalPage, referrer?: string): Record<string, unknown> {
    return this.parser.buildProperties(page, referrer, this.options.queryKeys, this.options.resolvePage);
  }

  private clearPending(): void {
    this.pending = undefined;
    if (this.readyTimer !== undefined) clearTimeout(this.readyTimer);
    this.readyTimer = undefined;
  }
}
